import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { useUWStore } from '@/shared/state/uwStore';
import { UWService } from '@/shared/api/services/uwService';
import SimpleLoadingScreen from '@/components/SimpleLoadingScreen';

// Blocks edit-screen routes (budget, onboarding, OB, direct-debit, review) once
// an application has moved past the editable phase. The user reaches an edit
// route via fresh navigation OR via browser-back from /uw/submitted; in both
// cases we redirect to the confirmation screen instead of allowing them to
// re-enter form data on a submitted record.
//
// The Zustand store does NOT persist `application` (only `applicationId` and
// section data), so on a cold mount we fetch via UWService.getApplication.
// On a warm mount we trust the store, but only if its applicationId matches
// the URL param — this prevents stale state from a previous application
// leaking through.

const EDITABLE_STATUSES = new Set(['CREATED', 'INVITE_SENT', 'ACTIVE', 'IN_PROGRESS']);

type CheckState = 'checking' | 'editable' | 'locked' | 'error';

// Mirrors uwRedirectForStatus in BlockUwFromGenericOnboarding — kept inline
// to avoid a shared util for two callers.
function lockedRedirectFor(applicationId: string, status: unknown): string {
  const s = typeof status === 'string' ? status.toUpperCase() : '';
  if (s === 'SUBMITTED' || s === 'UNDER_REVIEW') return `/uw/submitted/${applicationId}`;
  if (s === 'APPROVED' || s === 'DECLINED' || s === 'REFERRED') return `/uw/decision/${applicationId}`;
  if (s === 'AFFORDABILITY_DECLINED') return `/uw/affordability-decline/${applicationId}`;
  if (s === 'EXPIRED') return `/uw/expired/${applicationId}`;
  return `/uw/submitted/${applicationId}`;
}

export default function RequireApplicationEditable(): JSX.Element {
  const { applicationId } = useParams<{ applicationId: string }>();
  const application = useUWStore((s) => s.application);
  const setApplication = useUWStore((s) => s.setApplication);
  const [state, setState] = useState<CheckState>('checking');
  const [redirectTo, setRedirectTo] = useState<string>('');

  useEffect(() => {
    let cancelled = false;

    const check = async (): Promise<void> => {
      if (!applicationId) {
        if (!cancelled) setState('error');
        return;
      }

      // Trust the store only if it holds the same application as the URL.
      if (application && application.applicationId === applicationId) {
        if (!cancelled) {
          if (EDITABLE_STATUSES.has(application.status)) {
            setState('editable');
          } else {
            setRedirectTo(lockedRedirectFor(applicationId, application.status));
            setState('locked');
          }
        }
        return;
      }

      try {
        const app = await UWService.getApplication(applicationId);
        if (cancelled) return;
        setApplication(app);
        if (EDITABLE_STATUSES.has(app.status)) {
          setState('editable');
        } else {
          setRedirectTo(lockedRedirectFor(applicationId, app.status));
          setState('locked');
        }
      } catch {
        if (!cancelled) setState('error');
      }
    };

    void check();
    return () => {
      cancelled = true;
    };
    // We deliberately key on applicationId and the store's application id —
    // not the full application object — to avoid a refetch loop when
    // setApplication causes a new reference but the same id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, application?.applicationId]);

  if (state === 'checking') return <SimpleLoadingScreen title="Loading..." />;
  if (state === 'locked') return <Navigate to={redirectTo} replace />;
  // On fetch error fall back to /uw/offer (re-entry point of UW journey) rather
  // than /dashboard — UW customers are blocked from /dashboard by RequireAuth.
  if (state === 'error') return <Navigate to={`/uw/offer/${applicationId ?? ''}`} replace />;
  return <Outlet />;
}
